import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Input, Modal, Select, Table, message } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { useRecoilValue } from "recoil";
import { locationsState, memberState } from "../../../../state/atoms";
import { LocationDto } from "../../../../generated/dashboard-client";
import DataKitApi from "../../../../api/DataKitApi";
import { DataKitAssignmentDto, DataKitDto, UnassignPreviewDto } from "../../../../domain/DataKitDto";
import { DataKitThumb } from "./DataKitThumb";
import "./DataKitLocationAssignmentsTab.scss";

const PAGE_SIZE = 10;

type StatusFilter = "all" | "assigned" | "unassigned";

interface Row {
  location: LocationDto;
  kits: DataKitDto[];
}

interface Props {
  kits: DataKitDto[];
  onChanged: () => void;
}

export function DataKitLocationAssignmentsTab({ kits, onChanged }: Props) {
  const locations = useRecoilValue(locationsState);
  const member = useRecoilValue(memberState);
  const canWrite = !!member && (member.owner || member.role === "Admin");

  const [assignmentsByKit, setAssignmentsByKit] = useState<Record<string, DataKitAssignmentDto[]>>({});
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState<StatusFilter>("all");
  const [page, setPage] = useState(1);

  const [assignRow, setAssignRow] = useState<Row | null>(null);
  const [assignKitId, setAssignKitId] = useState<string | undefined>();
  const [saving, setSaving] = useState(false);

  const [unassignTarget, setUnassignTarget] = useState<{ row: Row; kit: DataKitDto } | null>(null);
  const [unassignPreview, setUnassignPreview] = useState<UnassignPreviewDto | null>(null);

  const activeKits = useMemo(() => kits.filter((k) => !k.archived), [kits]);

  const loadAssignments = useCallback(() => {
    const withIds = kits.filter((k) => !!k.id);
    if (withIds.length === 0) {
      setAssignmentsByKit({});
      return;
    }
    setLoading(true);
    Promise.all(
      withIds.map((k) =>
        DataKitApi.listAssignments(k.id as string)
          .then((res) => [k.id as string, res.data || []] as const)
          .catch(() => [k.id as string, [] as DataKitAssignmentDto[]] as const)
      )
    )
      .then((entries) => setAssignmentsByKit(Object.fromEntries(entries)))
      .finally(() => setLoading(false));
  }, [kits]);

  useEffect(() => {
    loadAssignments();
  }, [loadAssignments]);

  const kitsByLocation = useMemo(() => {
    const byId = new Map(kits.filter((k) => k.id).map((k) => [k.id as string, k]));
    const map = new Map<string, DataKitDto[]>();
    Object.entries(assignmentsByKit).forEach(([kitId, assignments]) => {
      const kit = byId.get(kitId);
      if (!kit) return;
      assignments
        .filter((a) => a.unitType === "LOCATION")
        .forEach((a) => {
          const list = map.get(a.unitId) || [];
          list.push(kit);
          map.set(a.unitId, list);
        });
    });
    return map;
  }, [assignmentsByKit, kits]);

  const rows: Row[] = useMemo(
    () =>
      locations.map((location) => ({
        location,
        kits: kitsByLocation.get(String(location.internalId)) || [],
      })),
    [locations, kitsByLocation]
  );

  const assignedCount = useMemo(() => rows.filter((r) => r.kits.length > 0).length, [rows]);
  const unassignedCount = rows.length - assignedCount;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter((r) => {
      if (q && !(r.location.name || "").toLowerCase().includes(q)) return false;
      if (status === "assigned" && r.kits.length === 0) return false;
      if (status === "unassigned" && r.kits.length > 0) return false;
      return true;
    });
  }, [rows, query, status]);

  useEffect(() => {
    setPage(1);
  }, [query, status]);

  const pageItems = useMemo(
    () => filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [filtered, page]
  );

  const openAssign = (row: Row) => {
    setAssignRow(row);
    setAssignKitId(undefined);
  };

  const confirmAssign = () => {
    if (!assignRow || !assignKitId) return;
    setSaving(true);
    DataKitApi.createAssignment(assignKitId, {
      unitType: "LOCATION",
      unitId: String(assignRow.location.internalId),
    })
      .then(() => {
        setAssignRow(null);
        loadAssignments();
        onChanged();
      })
      .catch(() => message.error("Could not assign the data kit"))
      .finally(() => setSaving(false));
  };

  const openUnassign = (row: Row, kit: DataKitDto) => {
    setUnassignTarget({ row, kit });
    setUnassignPreview(null);
    DataKitApi.previewUnassign(kit.id as string, "LOCATION", String(row.location.internalId))
      .then((res) => setUnassignPreview(res.data))
      .catch(() => setUnassignPreview(null));
  };

  const confirmUnassign = () => {
    if (!unassignTarget) return;
    const { row, kit } = unassignTarget;
    setSaving(true);
    DataKitApi.deleteAssignment(kit.id as string, "LOCATION", String(row.location.internalId))
      .then(() => {
        setUnassignTarget(null);
        loadAssignments();
        onChanged();
      })
      .catch(() => message.error("Could not remove the assignment"))
      .finally(() => setSaving(false));
  };

  const availableKits = useMemo(() => {
    if (!assignRow) return activeKits;
    const taken = new Set(assignRow.kits.map((k) => k.id));
    return activeKits.filter((k) => !taken.has(k.id));
  }, [activeKits, assignRow]);

  const columns = [
    {
      title: "Location",
      key: "location",
      render: (_: unknown, row: Row) => {
        const parts = [row.location.city, row.location.state].filter(Boolean);
        return (
          <div className="data-kit-location-cell">
            <div className="data-kit-location-name">{row.location.name}</div>
            {parts.length > 0 && (
              <div className="data-kit-location-address">{parts.join(", ")}</div>
            )}
          </div>
        );
      },
    },
    {
      title: "Data Kits",
      key: "kits",
      render: (_: unknown, row: Row) =>
        row.kits.length === 0 ? (
          <span className="data-kit-no-kit">No Data Kit assigned</span>
        ) : (
          <div className="data-kit-chips">
            {row.kits.map((kit) => (
              <span key={kit.id} className="data-kit-chip">
                <DataKitThumb name={kit.name || "Untitled"} size={20} />
                <span className="data-kit-chip-name">{kit.name || "Untitled"}</span>
                {canWrite && (
                  <button
                    type="button"
                    className="data-kit-chip-remove"
                    title={`Remove ${kit.name}`}
                    onClick={() => openUnassign(row, kit)}
                  >
                    ×
                  </button>
                )}
              </span>
            ))}
          </div>
        ),
    },
    {
      title: (
        <div className="data-kit-header-chips">
          <span className="data-kit-count-chip assigned">{assignedCount} assigned</span>
          <span className="data-kit-count-chip unassigned">{unassignedCount} unassigned</span>
        </div>
      ),
      key: "action",
      width: 160,
      align: "right" as const,
      render: (_: unknown, row: Row) =>
        canWrite ? (
          <Button className="data-kit-assign-btn" onClick={() => openAssign(row)}>
            Assign
          </Button>
        ) : null,
    },
  ];

  return (
    <div className="data-kit-location-assignments-tab">
      <div className="data-kit-assignments-toolbar">
        <Input
          className="data-kit-assignments-search"
          size="large"
          placeholder="Search locations"
          prefix={<SearchOutlined style={{ color: "#999" }} />}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          allowClear
        />
        <div className="data-kit-assignments-segment">
          {([
            { label: "All", value: "all" },
            { label: "Assigned", value: "assigned" },
            { label: "Unassigned", value: "unassigned" },
          ] as { label: string; value: StatusFilter }[]).map((opt) => (
            <button
              key={opt.value}
              type="button"
              className={`data-kit-segment-btn${status === opt.value ? " selected" : ""}`}
              onClick={() => setStatus(opt.value)}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      <Table
        className="data-kit-assignments-table"
        dataSource={pageItems}
        columns={columns}
        loading={loading}
        rowKey={(row) => String(row.location.internalId)}
        rowClassName={(row) => (row.kits.length > 0 ? "" : "data-kit-row-unassigned")}
        pagination={
          filtered.length > PAGE_SIZE
            ? {
                current: page,
                pageSize: PAGE_SIZE,
                total: filtered.length,
                onChange: setPage,
                showSizeChanger: false,
              }
            : false
        }
        locale={{ emptyText: "No locations match your filters" }}
      />

      <Modal
        className={"data-kit-assign-modal clean one"}
        open={!!assignRow}
        width={520}
        onCancel={() => setAssignRow(null)}
      >
        <header>
          <h2>Assign a Data Kit to {assignRow?.location.name || ""}</h2>
        </header>
        {availableKits.length === 0 ? (
          <p className="data-kit-assign-empty">
            Every data kit is already assigned to this location.
          </p>
        ) : (
          <Select
            className="data-kit-assign-select"
            size="large"
            placeholder="Choose a data kit"
            value={assignKitId}
            onChange={setAssignKitId}
            options={availableKits.map((k) => ({ label: k.name || "Untitled", value: k.id as string }))}
          />
        )}

        <footer>
          <Button size="large" onClick={() => setAssignRow(null)}>
            Cancel
          </Button>
          <Button
            size="large"
            className="primary"
            loading={saving}
            disabled={!assignKitId}
            onClick={confirmAssign}
          >
            Assign
          </Button>
        </footer>
      </Modal>

      <Modal
        className={"data-kit-unassign-modal clean one"}
        open={!!unassignTarget}
        width={520}
        onCancel={() => setUnassignTarget(null)}
      >
        <header>
          <h2>
            Remove {unassignTarget?.kit.name || ""} from{" "}
            {unassignTarget?.row.location.name || ""}?
          </h2>
        </header>
        {unassignPreview ? (
          <p className="data-kit-unassign-copy">
            This location will lose access to {unassignPreview.itemCount} item
            {unassignPreview.itemCount === 1 ? "" : "s"}.
            {unassignPreview.designCount > 0 && (
              <>
                {" "}
                {unassignPreview.placedItemCount} item
                {unassignPreview.placedItemCount === 1 ? "" : "s"} already placed on{" "}
                {unassignPreview.designCount} design
                {unassignPreview.designCount === 1 ? "" : "s"} will stay on those designs but stop
                receiving updates.
              </>
            )}
          </p>
        ) : (
          <p className="data-kit-unassign-copy">Checking what this location uses…</p>
        )}

        <footer>
          <Button size="large" onClick={() => setUnassignTarget(null)}>
            Cancel
          </Button>
          <Button size="large" danger loading={saving} onClick={confirmUnassign}>
            Remove
          </Button>
        </footer>
      </Modal>
    </div>
  );
}
